import { UnifiedAlarm } from '@hakimo-ui/hakimo/types';

// The camera-describing fields of an alarm: exactly what a door-only copy of an
// enterprise alarm is missing.
export type DoorCamera = Pick<
  UnifiedAlarm,
  | 'cameraId'
  | 'cameraName'
  | 'cameraGroupId'
  | 'cameraGroupName'
  | 'cameraLiveStreamUrl'
  | 'cameraPlayBackUrl'
  | 'isTalkDownEnabled'
>;

// Door id -> the camera that watches it, or `null` for a door the group knows
// about but whose camera it has not seen (the door is then its own row).
export type DoorToCameraMap = Map<string, DoorCamera | null>;

/**
 * An enterprise alarm is raised on a door. The copy that comes with the
 * location alarm carries the camera that watches the door, but the minor and
 * neighbour copies fetched by door id do not, so their `cameraId` is the door's
 * id and the timeline grows a second row for the same camera.
 *
 * Learns every door the group's alarms were raised on (`door`), and for each
 * the camera from the first copy that carries one (`cameraId` other than the
 * door). Remote guarding and scan alarms never set `door`, so their map is
 * empty and nothing downstream touches them.
 */
export function buildDoorToCameraMap(alarms: UnifiedAlarm[]): DoorToCameraMap {
  const doorToCamera: DoorToCameraMap = new Map();
  for (const alarm of alarms) {
    const doorId = alarm?.door?.id;
    if (!doorId) {
      continue;
    }
    if (alarm.cameraId !== doorId && !doorToCamera.get(doorId)) {
      doorToCamera.set(doorId, {
        cameraId: alarm.cameraId,
        cameraName: alarm.cameraName,
        cameraGroupId: alarm.cameraGroupId,
        cameraGroupName: alarm.cameraGroupName,
        cameraLiveStreamUrl: alarm.cameraLiveStreamUrl,
        cameraPlayBackUrl: alarm.cameraPlayBackUrl,
        isTalkDownEnabled: alarm.isTalkDownEnabled,
      });
    } else if (!doorToCamera.has(doorId)) {
      doorToCamera.set(doorId, null);
    }
  }
  return doorToCamera;
}

/**
 * Most door alarms have no video (prod, 7 days: ~72% at Workday, ~97% at
 * Elevance). With no video window every source stamps them with the time they
 * were loaded, which puts them at the wrong place on the timeline, collapses a
 * whole neighbour response into one entry (same camera, same instant) and lets
 * the group's own alarm come back as its neighbour. Place them at the alarm's
 * own time instead. Frames and real video windows are left alone.
 */
function placeAtAlarmTimeIfNoVideo(alarm: UnifiedAlarm): UnifiedAlarm {
  if (alarm.media?.type === 'frames' || alarm.media?.video?.startTime) {
    return alarm;
  }
  const alarmTime = Date.parse(alarm.timestamp);
  if (
    !Number.isFinite(alarmTime) ||
    (alarm.alarmStartTime === alarmTime && alarm.alarmEndTime === alarmTime)
  ) {
    return alarm;
  }
  return { ...alarm, alarmStartTime: alarmTime, alarmEndTime: alarmTime };
}

/**
 * Normalises one door alarm; anything else is returned untouched.
 * - `cameraId` is a door the group knows a camera for: moved onto that camera,
 *   and its time fixed. These are the copies fetched by door id, whether or
 *   not they carry `door`: event-flow's neighbours leave it unset, the LA
 *   page's minors from `/orm/alarm` set it.
 * - Any other door alarm (carries `door`, or `cameraId` is a door with no
 *   known camera): stays where it is, only its time is fixed.
 * Matches on `cameraId`, not `door`: that is the one thing every copy has,
 * wherever it was fetched from.
 */
export function remapAlarmToCamera(
  alarm: UnifiedAlarm,
  doorToCamera: DoorToCameraMap
): UnifiedAlarm {
  const camera = doorToCamera.get(alarm.cameraId);
  if (camera) {
    return placeAtAlarmTimeIfNoVideo({ ...alarm, ...camera });
  }
  if (alarm.door?.id || doorToCamera.has(alarm.cameraId)) {
    return placeAtAlarmTimeIfNoVideo(alarm);
  }
  return alarm;
}

/**
 * Normalises the door alarms (see `remapAlarmToCamera`) and drops repeats of an
 * alarm id, keeping the copy that already had the camera and otherwise the
 * first. Order is by first appearance. Returns `alarms` itself when there is
 * nothing to do, so callers that memoise on it keep a stable reference; a group
 * with no door at all (remote guarding, scan) always comes back as it went in.
 */
export function foldDoorAlarmsOntoCameras(
  alarms: UnifiedAlarm[],
  doorToCamera: DoorToCameraMap
): UnifiedAlarm[] {
  if (doorToCamera.size === 0) {
    return alarms;
  }
  const byId = new Map<string, { alarm: UnifiedAlarm; moved: boolean }>();
  for (const alarm of alarms) {
    // A single bad entry must not blank the whole group (see the dedupe
    // helpers in unified-alarm-group/utils).
    if (!alarm) {
      continue;
    }
    const folded = remapAlarmToCamera(alarm, doorToCamera);
    const moved = folded.cameraId !== alarm.cameraId;
    const existing = byId.get(alarm.id);
    if (!existing || (existing.moved && !moved)) {
      byId.set(alarm.id, { alarm: folded, moved });
    }
  }
  const result = Array.from(byId.values(), (entry) => entry.alarm);
  const unchanged =
    result.length === alarms.length &&
    result.every((alarm, index) => alarm === alarms[index]);
  return unchanged ? alarms : result;
}
